import React from 'react'
import {
  ServerSettingsDialogProps,
  ServerSettingsMutationProps
} from './shared'
import { Mutation } from 'react-apollo'
import gql from 'graphql-tag'
import { SingleServerSettings } from '../servers-grid/shared'
import { ServerSettingsLoader } from './server-settings-loader'
import { logSuccess, logError } from '../toaster'
import { getSingleServerSettings } from './get-single-server-settings'
import { ServerSettingsFormSingle } from './server-settings-form-single'
import { ContrastFormattedMessage } from '../contrast-formatted-message'

const singleServerSettingsMutationName = 'updateServerSettings'

export const modifySingleServerSettingsMutation = gql`
  mutation UpdateServerSettings(
    $organizationID: ID!
    $id: ID!
    $settings: SingleServerSettingsArg!
  ) {
    ${singleServerSettingsMutationName} (
      input: { organizationID: $organizationID, id: $id, settings: $settings }
    ) {
      id
      name
      logPath
      environment
      logLevel
      botProtection
      stacktraceCaptureMode
      samplingProfile
      assess
      protect
      syslogConfig {
        enabled
        ipAddress
        port
        facilityCode
        protocol
        severityExploited
        severitySuspicious
        severityBlocked
        severityBlockedPerimeter
        severityProbed
      }
      sampling {
        enabled
        frequency
        baseline
        window
      }
    }
  }
`

interface MutationData {
  readonly __typename: 'Mutation'
  readonly [singleServerSettingsMutationName]: {
    readonly _placeholder: true
    readonly __typename: 'ServerSettingsMutationArg'
  }
}

interface MutationVariables {
  readonly settings: SingleServerSettings
  readonly organizationID: string
  readonly id: string
}

export function ModifySingleServerSettingsDialog({
  organizationID,
  serverID,
  refetchQueries,
  refresh,
  closeDialog,
  isOpen,
  protect: userIsProtect,
  agentFeatures,
  isFreemium,
  isReadOnly
}: ServerSettingsDialogProps &
  ServerSettingsMutationProps & {
    serverID: string
    isReadOnly?: boolean
  }) {
  return (
    <Mutation<MutationData, MutationVariables>
      mutation={modifySingleServerSettingsMutation}
      ignoreResults
    >
      {(mutate) => {
        return (
          <ContrastFormattedMessage id="detail_servers_successfullySavedSettings">
            {(successMessage) => (
              <ContrastFormattedMessage id="detail_servers_errorSavingSettings">
                {(errorMessage) => (
                  <ServerSettingsLoader
                    organizationID={organizationID}
                    serverID={serverID}
                  >
                    {({ loading, server, refetch }) => {
                      return (
                        !loading && (
                          <ServerSettingsFormSingle
                            isReadOnly={isReadOnly}
                            server={server}
                            onConfirm={(formValue) => {
                              mutate({
                                refetchQueries,
                                variables: {
                                  settings: getSingleServerSettings(
                                    formValue,
                                    server
                                  ),
                                  organizationID,
                                  id: serverID
                                },
                                optimisticResponse: {
                                  [singleServerSettingsMutationName]: {
                                    _placeholder: true,
                                    __typename: 'ServerSettingsMutationArg'
                                  },
                                  __typename: 'Mutation'
                                },
                                update: () => false // TODO TS-2908
                              })
                                .then(() => {
                                  logSuccess(successMessage)
                                  // eslint-disable-next-line @typescript-eslint/no-floating-promises
                                  refresh()
                                  refetch()
                                })
                                .catch((error) => {
                                  console.error(error)
                                  logError(errorMessage)
                                  return error
                                })
                              closeDialog()
                            }}
                            onCancel={closeDialog}
                            isOpen={isOpen}
                            agentFeatures={agentFeatures}
                            userIsProtect={userIsProtect}
                            isFreemium={isFreemium}
                          />
                        )
                      )
                    }}
                  </ServerSettingsLoader>
                )}
              </ContrastFormattedMessage>
            )}
          </ContrastFormattedMessage>
        )
      }}
    </Mutation>
  )
}
